import { ModelTier } from "@qw/graphql";
import { PlaybookType } from "@qw/qw-common";
import { FieldSelect, type FieldSelectOption, Group } from "@qw/ui";
import { type FC, useMemo, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { MODEL_TIER_RUNTIME_DEFAULT, type PlaybookFormValues } from "./playbook-editor.types";

/**
 * What `playbook-execution-runtime` runs a playbook on when it declares no tier: Alby's
 * dedicated playbook tier, which is not itself a declarable `ModelTier`.
 */
const RUNTIME_DEFAULT_TIER_LABEL = "Claude Opus 5.5";

/**
 * Display name for every tier the API accepts. Exhaustive on purpose: a tier added to
 * `ModelTier` fails to compile here, forcing a decision about whether it belongs in the
 * curated list below rather than silently becoming a value this control cannot name.
 */
const MODEL_TIER_LABELS: Record<ModelTier, string> = {
  [ModelTier.Nano]: "Nano",
  [ModelTier.Micro]: "Micro",
  [ModelTier.Small]: "Small",
  [ModelTier.Standard]: "Standard",
  [ModelTier.Advanced]: "Advanced",
  [ModelTier.Expert]: "Expert",
};

/**
 * The tiers offered in the editor, deliberately a subset of the six the API accepts.
 *
 * The cost ladder is steeply non-linear and the cheap end drops extended reasoning
 * (`model-registry.types.ts`: "Lower tiers (NANO, MICRO, SMALL) use simpler models
 * without extended reasoning"), so the tiers that save real money are the ones least
 * able to finish an agentic playbook. Offering all six as an even-looking scale implies
 * a smooth tradeoff that does not exist — Standard saves ~1.8x on output where Micro
 * saves ~12x. Three options make the actual decision legible: stay put, step down
 * safely, or go cheap and accept the risk.
 *
 * Nano is left out as a trap rather than an option: 100x cheaper, but gpt-5-nano has no
 * reasoning budget and will not drive a multi-step tool-using workflow. Small and
 * Standard are left out as refinements between Balanced and Economy that there is no
 * feedback loop to choose between. A playbook already holding any of the three appends
 * its own option (see `uncuratedTier`), so nothing becomes unreadable or unsettable.
 */
const CURATED_TIERS: readonly ModelTier[] = [ModelTier.Advanced, ModelTier.Micro];

const isUncuratedTier = (value: PlaybookFormValues["modelTier"]): value is ModelTier =>
  value !== MODEL_TIER_RUNTIME_DEFAULT && !CURATED_TIERS.includes(value);

const MODEL_TIER_OPTIONS: readonly FieldSelectOption[] = [
  {
    value: MODEL_TIER_RUNTIME_DEFAULT,
    label: "Default",
    description: `Follow the runtime default for playbooks (${RUNTIME_DEFAULT_TIER_LABEL} today). High reasoning effort.`,
  },
  {
    value: ModelTier.Advanced,
    label: "Balanced",
    description: "Advanced tier (Claude Sonnet 5.5). Cheaper than the default and still reasons at length.",
  },
  {
    value: ModelTier.Micro,
    label: "Economy",
    description:
      "Micro tier. Roughly 12-20x cheaper, but no extended reasoning — long or multi-step playbooks may stall or finish badly.",
  },
];

interface PlaybookModelTierFieldProps {
  canEdit: boolean;
}

export const PlaybookModelTierField: FC<PlaybookModelTierFieldProps> = ({ canEdit }) => {
  const { control } = useFormContext<PlaybookFormValues>();
  const modelTier = useWatch<PlaybookFormValues, "modelTier">({ name: "modelTier", control });
  const playbookType = useWatch<PlaybookFormValues, "type">({ name: "type", control });

  // Knowledge playbooks are embedded in the system prompt as context and are never
  // executed, so they run no frames of their own and have no tier to pick.
  const isKnowledge = playbookType === PlaybookType.Knowledge;

  // A tier set outside the editor (B-1774 set Orbit's front door with a hand-written
  // mutation) may not be one of the curated three. Show it rather than misreport the
  // playbook as running at something it is not — the whole point of surfacing the field.
  //
  // Read off the value this playbook loaded with, not the live selection: the editor
  // autosaves and has no undo, so deriving it from the current value would make the
  // option vanish the instant you picked anything else, turning one stray click into a
  // one-way exit that only a hand-written mutation could reverse.
  const loadedTier = useRef(modelTier).current;
  const uncuratedTier = isUncuratedTier(loadedTier) ? loadedTier : null;

  const options = useMemo(
    () =>
      uncuratedTier
        ? [
            ...MODEL_TIER_OPTIONS,
            {
              value: uncuratedTier,
              label: MODEL_TIER_LABELS[uncuratedTier],
              description: "Set outside the editor. Listed so this playbook's original tier stays selectable.",
            },
          ]
        : MODEL_TIER_OPTIONS,
    [uncuratedTier]
  );

  const selectedLabel = options.find((option) => option.value === modelTier)?.label;

  return (
    <Group className="space-y-1">
      <FieldSelect label="Model Tier" name="modelTier" disabled={!canEdit || isKnowledge} options={options} />

      <p className="text-[11px] text-muted-foreground">
        {isKnowledge
          ? "Contextual Background Knowledge playbooks never execute, so the tier has no effect."
          : modelTier === MODEL_TIER_RUNTIME_DEFAULT
            ? `Running at the runtime default (${RUNTIME_DEFAULT_TIER_LABEL}), and will follow it if it changes.`
            : `Pinned to ${selectedLabel}, regardless of the runtime default.`}
      </p>
    </Group>
  );
};
